import { employeeServiceApi } from "../../app/employeeServiceApi";
import { API_URL } from "../../constants/apiUrl";
import {
  EmployeesListExportParams,
  GetAllJobPostParamsType,
  GetAllJobPostResponseType,
  GetAppliedJobParamsType,
  GetAppliedJobResponseType,
  GetBusinessDataResponseType,
  GetEmployeeDetailsResponseType,
  GetEmployeeManagementDataParamsType,
  GetEmployeeManagementDataResponseType,
  GetEmployeeParamsType,
  GetEmployeesListExportResponseType,
  GetEmployeesResponseType,
  GetInterviewListParamsType,
  GetInterviewListResponseType,
  GetJobDetailsResponseType,
  GetQueryArgsType,
  PatchQueryArgsType,
} from "../../types";

export const employeeApi = employeeServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getEmployees: builder.query<
      GetEmployeesResponseType,
      GetQueryArgsType<GetEmployeeParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getEmployees,
        ...queryArgs,
      }),
      providesTags: ["GetEmployees"],
    }),
    getEmployeeDetails: builder.query<GetEmployeeDetailsResponseType, string>({
      query: (id) => ({
        url: `${API_URL.getEmployeeDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetEmployeeDetails", id }],
    }),
    getMe: builder.query<GetEmployeeDetailsResponseType, void>({
      query: () => ({
        url: API_URL.getMe,
      }),
      providesTags: () => [{ type: "GetEmployeeDetails" }],
    }),
    addEmployee: builder.mutation({
      query: (data) => ({
        url: API_URL.addEmployee,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetEmployees"];
        return [];
      },
    }),
    updateProfilePicture: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateProfilePicture}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeeOfficialData: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateEmployeeOfficialData}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeePersonalData: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateEmployeePersonalData}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeeFinancialData: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateEmployeeFinancialData}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeeSalaryData: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateEmployeeSalaryData}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeeEmergencyContact: builder.mutation<
      unknown,
      PatchQueryArgsType
    >({
      query: ({ body, id }) => ({
        url: `${API_URL.updateEmployeeEmergencyContact}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeeAdditionalData: builder.mutation<unknown, PatchQueryArgsType>(
      {
        query: ({ body, id }) => ({
          url: `${API_URL.updateEmployeeAdditionalData}/${id}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (result, _, { id }) => {
          if (result)
            return [
              "GetEmployees",
              {
                type: "GetEmployeeDetails",
                id,
              },
            ];

          return [];
        },
      }
    ),
    updateEmployeeStatus: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateEmployeeStatus}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateEmployeeBulkStatus: builder.mutation({
      query: (body) => ({
        url: API_URL.updateEmployeeBulkStatus,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetEmployees", "GetEmployeeDetails"];

        return [];
      },
    }),
    sendInvite: builder.mutation({
      query: (data) => ({
        url: API_URL.sendInvite,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetEmployees"];

        return [];
      },
    }),
    uploadEmployeeBkashData: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadEmployeeBkashData,
        method: "PATCH",
        body: data,
      }),
    }),
    uploadPhotoInEmployee: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.uploadPhotoInEmployee,
          method: "PATCH",
          body: data,
        };
      },
    }),
    getBusinessData: builder.query<GetBusinessDataResponseType, string>({
      query: (id) => ({
        url: `${API_URL.getBusinessData}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetBusinessData", id }],
    }),
    reInvited: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.reInvited}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    activeInvited: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.activeInvited}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployees",
            {
              type: "GetEmployeeDetails",
              id,
            },
          ];

        return [];
      },
    }),
    deleteInvited: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `${API_URL.deleteInvited}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetEmployees"];

        return [];
      },
    }),
    downloadEmployeeDetails: builder.query({
      query: ({ id }) => ({
        url: `${API_URL.downloadEmployeeDetails}/${id}`,
        responseHandler: async (response: any) => {
          const blob = await response.blob(); // Convert response to blob
          return window.URL.createObjectURL(blob); // Create object URL
        },
        cache: "no-cache",
      }),
    }),
    getEmployeesListExport: builder.query<
      GetEmployeesListExportResponseType,
      GetQueryArgsType<EmployeesListExportParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getEmployeesListExport,
        params: {
          ...queryArgs,
        },
      }),
    }),

    getEmployeeManagementData: builder.query<
      GetEmployeeManagementDataResponseType,
      GetQueryArgsType<GetEmployeeManagementDataParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getEmployeeManagementData,
        ...queryArgs,
      }),
      providesTags: ["GetEmployees"],
    }),
    uploadEmployeeInfo: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadEmployeeInfo,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetEmployees"];

        return [];
      },
    }),

    getAllJobPost: builder.query<
      GetAllJobPostResponseType,
      GetQueryArgsType<GetAllJobPostParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllJobPost,
        ...queryArgs,
      }),
      providesTags: ["GetAllJobPost"],
    }),
    addJob: builder.mutation({
      query: (data) => ({
        url: API_URL.addJob,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllJobPost"];
        return [];
      },
    }),
    getJobDetails: builder.query<GetJobDetailsResponseType, string>({
      query: (id) => ({
        url: `${API_URL.getJobDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetJobDetails", id }],
    }),
    deleteJob: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.deleteJob}/${id}`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result) return ["GetAllJobPost", { type: "GetJobDetails", id }];
        return [];
      },
    }),
    updateJob: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateJob}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result) return ["GetAllJobPost", { type: "GetJobDetails", id }];
        return [];
      },
    }),
    updateJobStatus: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateJobStatus}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result) return ["GetAllJobPost", { type: "GetJobDetails", id }];
        return [];
      },
    }),
    updateStatusJob: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateStatusJob}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result) return ["GetAllJobPost", { type: "GetJobDetails", id }];
        return [];
      },
    }),
    getAppliedJob: builder.query<
      GetAppliedJobResponseType,
      GetQueryArgsType<GetAppliedJobParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAppliedJob,
        ...queryArgs,
      }),
      providesTags: ["GetAppliedJob"],
    }),
    applyJob: builder.mutation({
      query: (data) => ({
        url: API_URL.applyJob,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAppliedJob"];
        return [];
      },
    }),
    jobShortlistOrReject: builder.mutation({
      query: (data) => ({
        url: API_URL.jobShortlistOrReject,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAppliedJob"];
        return [];
      },
    }),
    getInterviewList: builder.query<
      GetInterviewListResponseType,
      GetQueryArgsType<GetInterviewListParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getInterviewList,
        ...queryArgs,
      }),
      providesTags: ["GetInterviewList"],
    }),
    createInterview: builder.mutation({
      query: (data) => ({
        url: API_URL.createInterview,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetInterviewList"];
        return [];
      },
    }),
    interviewBulkStatus: builder.mutation({
      query: (data) => ({
        url: API_URL.interviewBulkStatus,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetInterviewList"];
        return [];
      },
    }),
    interviewBulkOfferStatus: builder.mutation({
      query: (data) => ({
        url: API_URL.interviewBulkOfferStatus,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetInterviewList"];
        return [];
      },
    }),

    syncCheckIn: builder.mutation({
      query: (data) => ({
        url: API_URL.syncCheckIn,
        method: "POST",
        body: data,
      }),
    }),

    syncCheckOut: builder.mutation({
      query: (data) => ({
        url: API_URL.syncCheckOut,
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetEmployeesQuery,
  useGetEmployeeDetailsQuery,
  useAddEmployeeMutation,
  useUpdateProfilePictureMutation,
  useUpdateEmployeeOfficialDataMutation,
  useUpdateEmployeePersonalDataMutation,
  useUpdateEmployeeFinancialDataMutation,
  useUpdateEmployeeSalaryDataMutation,
  useUpdateEmployeeEmergencyContactMutation,
  useUpdateEmployeeAdditionalDataMutation,
  useUpdateEmployeeStatusMutation,
  useUpdateEmployeeBulkStatusMutation,
  useSendInviteMutation,
  useUploadEmployeeBkashDataMutation,
  useUploadPhotoInEmployeeMutation,
  useGetBusinessDataQuery,
  useActiveInvitedMutation,
  useDeleteInvitedMutation,
  useReInvitedMutation,
  useLazyDownloadEmployeeDetailsQuery,
  useGetEmployeesListExportQuery,
  useGetEmployeeManagementDataQuery,
  useDownloadEmployeeDetailsQuery,
  useUploadEmployeeInfoMutation,
  useGetMeQuery,
  useGetAllJobPostQuery,
  useAddJobMutation,
  useGetJobDetailsQuery,
  useGetAppliedJobQuery,
  useApplyJobMutation,
  useJobShortlistOrRejectMutation,
  useDeleteJobMutation,
  useUpdateJobMutation,
  useUpdateJobStatusMutation,
  useUpdateStatusJobMutation,
  useCreateInterviewMutation,
  useGetInterviewListQuery,
  useInterviewBulkStatusMutation,
  useInterviewBulkOfferStatusMutation,
  useSyncCheckInMutation,
  useSyncCheckOutMutation,
} = employeeApi;
